import { Link } from 'react-router-dom'
import { Menu, Search, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useSidebar } from './SidebarContext'
import { BrandMark } from './AppSidebar'

// TailAdmin AppHeader: sticky white bar with a bottom border. With a sidebar it shows the 44px
// sidebar toggle and the search field on the left; without one (Garage/EnergyOS workspaces) the
// brand sits on the left instead. Right side holds workspace actions and the user menu.
export function AppHeader({ withSidebar, brand, homeTo, left, right, search }: {
  withSidebar: boolean
  brand?: { name: string; icon: LucideIcon | string }
  homeTo?: string
  left?: ReactNode
  right?: ReactNode
  search?: boolean
}) {
  return (
    <header className="sticky top-0 z-[999] flex w-full border-b border-gray-200 bg-white">
      <div className="flex grow items-center justify-between gap-3 px-3 py-3 sm:gap-4 lg:px-6 lg:py-4">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          {withSidebar ? <SidebarToggle /> : brand && (
            <Link to={homeTo ?? '/'} className="flex shrink-0 items-center gap-3">
              <BrandMark icon={brand.icon} />
              <span className="hidden text-xl font-semibold text-gray-900 sm:block">{brand.name}</span>
            </Link>
          )}
          {withSidebar && brand && (
            <Link to={homeTo ?? '/'} className="xl:hidden"><BrandMark icon={brand.icon} /></Link>
          )}
          {search && (
            <div className="relative hidden xl:block">
              <Search size={20} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search or type command..."
                className="h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring-[3px] focus:ring-brand-500/10 xl:w-[430px]"
              />
              <kbd className="absolute right-2.5 top-1/2 inline-flex -translate-y-1/2 items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 px-[7px] py-[4.5px] font-outfit text-xs tracking-[-0.2px] text-gray-500">
                ⌘ K
              </kbd>
            </div>
          )}
          {left}
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">{right}</div>
      </div>
    </header>
  )
}

function SidebarToggle() {
  const { isMobileOpen, toggleSidebar } = useSidebar()
  return (
    <button
      onClick={toggleSidebar}
      aria-label="Toggle sidebar"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 lg:h-11 lg:w-11 xl:border xl:border-gray-200"
    >
      {isMobileOpen ? <X size={24} /> : <Menu size={20} />}
    </button>
  )
}

// TailAdmin header icon button (notifications / theme toggle slot): round, 44px, gray-200 border.
export function HeaderIconButton({ href, title, icon: Icon, onClick }: {
  href?: string
  title: string
  icon: LucideIcon
  onClick?: () => void
}) {
  const cls = 'relative flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700'
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" title={title} aria-label={title} className={cls}><Icon size={20} /></a>
  }
  return <button onClick={onClick} title={title} aria-label={title} className={cls}><Icon size={20} /></button>
}
